'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  article: string;
  weight: number;
  recommendation: string;
  toolRef?: string;
}

interface Section {
  id: string;
  title: string;
  description: string;
  icon: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'services',
    title: 'Services',
    description: 'Identification and mapping of important business services from a customer-harm perspective',
    icon: 'S',
    questions: [
      { id: 'sv1', text: 'Have you identified your important business services?', article: 'FCA PS21/3 §3 / DORA Art. 3(22)', weight: 10, recommendation: 'Create a register of important business services starting from customer outcomes, not internal systems. Map each service to the customers and markets it supports.', toolRef: '/tools/dependency-mapper' },
      { id: 'sv2', text: 'Are services defined from the perspective of customer harm, not internal systems?', article: 'FCA PS21/3 §3.7', weight: 10, recommendation: 'Reframe service definitions around what customers experience when disrupted. Ask "what harm would customers suffer?" rather than "which systems would be down?".' },
      { id: 'sv3', text: 'Have you identified customer groups affected by each service?', article: 'FCA SS1/21 §4', weight: 9, recommendation: 'For each important business service, document the specific customer segments, counterparties, and market participants who depend on it.' },
      { id: 'sv4', text: 'Is there a process for regularly reviewing and updating the service register?', article: 'FCA PS21/3 §3.12', weight: 8, recommendation: 'Establish a cadence (at least annual) for reviewing important business services. Trigger ad-hoc reviews when launching new products or making significant changes.' },
      { id: 'sv5', text: 'Are services mapped to underlying people, processes, technology, facilities, and data?', article: 'FCA SS1/21 §5 / DORA Art. 8', weight: 10, recommendation: 'Build end-to-end dependency maps for each important business service covering all resource types. This is the foundation for identifying vulnerabilities.', toolRef: '/tools/archimate' },
      { id: 'sv6', text: 'Are third-party providers mapped as dependencies for each service?', article: 'FCA SS1/21 §5 / DORA Art. 28(3)', weight: 9, recommendation: 'Maintain a register of all third-party providers per service, including cloud infrastructure, SaaS tools, data providers, and outsourced functions.', toolRef: '/tools/vendor-risk-assessment' },
      { id: 'sv7', text: 'Has the board approved the list of important business services?', article: 'FCA PS21/3 §3.3 / DORA Art. 5(2)', weight: 9, recommendation: 'Present the important business service register to the board for formal approval. Document the board discussion and rationale for inclusions/exclusions.' },
      { id: 'sv8', text: 'Do you have a methodology for prioritising services by harm severity?', article: 'FCA SS1/21 §4.3', weight: 8, recommendation: 'Develop a scoring methodology that considers number of affected customers, financial impact, market integrity implications, and regulatory obligations.' },
      { id: 'sv9', text: 'Are service boundaries clearly defined (where one service ends and another begins)?', article: 'FCA PS21/3 §3.10', weight: 7, recommendation: 'Document clear boundaries for each service. Avoid overlap that could lead to gaps in resilience coverage or confusion during incidents.' },
    ],
  },
  {
    id: 'tolerances',
    title: 'Tolerances',
    description: 'Setting and calibrating impact tolerances for each important business service',
    icon: 'T',
    questions: [
      { id: 'tl1', text: 'Have you set impact tolerances for each important business service?', article: 'FCA SS1/21 §6 / DORA Art. 11(1)', weight: 10, recommendation: 'Define the maximum tolerable level of disruption for each important business service. These must be specific, measurable, and set at the point beyond which harm becomes intolerable.', toolRef: '/tools/sla-generator' },
      { id: 'tl2', text: 'Are tolerances expressed as maximum tolerable disruption (not just RTO)?', article: 'FCA SS1/21 §6.3', weight: 10, recommendation: 'Impact tolerances should go beyond recovery time objectives. Express them in terms of maximum duration, maximum data loss, and maximum degradation customers would experience.' },
      { id: 'tl3', text: 'Do tolerances include time-based metrics: maximum outage and maximum degraded-mode duration?', article: 'FCA SS1/21 §6.5 / DORA Art. 11(4)', weight: 9, recommendation: 'Set explicit time limits for both total outage and degraded service. For example: "payments must not be unavailable for more than 2 hours or degraded for more than 4 hours."' },
      { id: 'tl4', text: 'Do tolerances include data integrity limits (maximum acceptable data loss/corruption)?', article: 'FCA SS1/21 §6.6 / DORA Art. 11(4)', weight: 9, recommendation: 'Define acceptable data loss thresholds. Consider both recovery point objectives and data correctness requirements for each service.' },
      { id: 'tl5', text: 'Are tolerances calibrated to severe-but-plausible disruption scenarios?', article: 'FCA SS1/21 §6.8', weight: 9, recommendation: 'Test tolerances against realistic scenarios like cloud region failure, key vendor outage, or cyberattack — not just minor incidents.', toolRef: '/tools/business-continuity-planner' },
      { id: 'tl6', text: 'Has the board approved the impact tolerances?', article: 'FCA SS1/21 §6.2 / DORA Art. 5(2)', weight: 8, recommendation: 'Present impact tolerances to the board with clear rationale for each threshold. Board members must understand and challenge the tolerances set.' },
      { id: 'tl7', text: 'Do tolerances include throughput/volume metrics where relevant?', article: 'FCA SS1/21 §6.7', weight: 7, recommendation: 'For volume-dependent services (e.g., payment processing), set minimum throughput levels during disruption. E.g., "must process at least 60% of peak volume during degraded mode."' },
      { id: 'tl8', text: 'Are tolerances regularly reviewed and recalibrated?', article: 'FCA SS1/21 §6.10 / DORA Art. 11(6)', weight: 8, recommendation: 'Review tolerances at least annually and after significant incidents, business changes, or regulatory updates. Document the review process and any changes made.' },
      { id: 'tl9', text: 'Can you demonstrate that tolerances are set at the point beyond which harm becomes intolerable?', article: 'FCA SS1/21 §6.4', weight: 8, recommendation: 'Document the analysis showing how tolerance levels were derived. Regulators expect evidence that thresholds reflect actual customer harm, not just operational convenience.' },
    ],
  },
  {
    id: 'architecture',
    title: 'Architecture',
    description: 'Identifying and mitigating single points of failure, enabling failure isolation and deterministic recovery',
    icon: 'A',
    questions: [
      { id: 'ar1', text: 'Have you identified single points of failure (SPOFs) for each important business service?', article: 'FCA SS1/21 §5.6 / DORA Art. 8(4)', weight: 10, recommendation: 'Conduct SPOF analysis for every important business service. Map each dependency and identify where a single failure would cause service disruption.', toolRef: '/tools/dependency-mapper' },
      { id: 'ar2', text: 'Is there a plan to eliminate or mitigate each identified SPOF?', article: 'FCA SS1/21 §5.7 / DORA Art. 9', weight: 10, recommendation: 'Create a remediation plan for each SPOF with options ranging from elimination (redundancy) to mitigation (manual fallback). Prioritise by impact tolerance risk.' },
      { id: 'ar3', text: 'Does your architecture prevent cascading failures across services (failure isolation)?', article: 'FCA SS1/21 §5.8 / DORA Art. 9(2)', weight: 9, recommendation: 'Implement bulkheads, circuit breakers, and service isolation patterns. Ensure that failure in one service cannot propagate to others through shared dependencies.' },
      { id: 'ar4', text: 'Do services support graceful degradation when dependencies are impaired?', article: 'FCA SS1/21 §5.9 / DORA Art. 11(2)', weight: 9, recommendation: 'Design services to continue operating in a reduced capacity when dependencies fail. Define and implement specific degraded-mode behaviours for each service.' },
      { id: 'ar5', text: 'Are dependencies tiered by criticality (critical, important, best-effort)?', article: 'DORA Art. 8(3)', weight: 8, recommendation: 'Classify each dependency by its impact on the service. Critical dependencies require redundancy; important ones need fallbacks; best-effort can be temporarily unavailable.' },
      { id: 'ar6', text: 'Are recovery procedures deterministic (documented, tested, automated where possible)?', article: 'DORA Art. 11(3) / FCA SS1/21 §5.10', weight: 9, recommendation: 'Create runbooks for every recovery scenario. Automate recovery steps where possible. Ensure procedures are tested regularly and updated after changes.', toolRef: '/tools/incident-response-planner' },
      { id: 'ar7', text: 'Do third-party/cloud dependencies have resilience measures (multi-region, exit plans)?', article: 'DORA Art. 28(8) / FCA SS1/21 §5.11', weight: 9, recommendation: 'Ensure critical cloud services are deployed across multiple regions. Maintain exit plans for key providers with documented migration procedures.', toolRef: '/tools/vendor-risk-assessment' },
      { id: 'ar8', text: 'Does your change management process prevent the introduction of new SPOFs?', article: 'DORA Art. 9(4)', weight: 7, recommendation: 'Add resilience review to change management gates. Require SPOF assessment for any change that modifies service architecture or introduces new dependencies.' },
      { id: 'ar9', text: 'Are backup and recovery capabilities tested against RPO/RTO targets?', article: 'DORA Art. 12 / FCA SS1/21 §5.12', weight: 9, recommendation: 'Conduct regular backup restoration tests. Measure actual recovery times and data loss against stated objectives. Address any gaps found.' },
      { id: 'ar10', text: 'Is architecture documentation current and includes resilience controls?', article: 'DORA Art. 8(1)', weight: 7, recommendation: 'Keep architecture diagrams and documentation up to date. Ensure resilience controls (redundancy, failover, circuit breakers) are clearly documented.', toolRef: '/tools/archimate' },
    ],
  },
  {
    id: 'monitoring',
    title: 'Monitoring',
    description: 'Service-level monitoring against impact tolerances with real-time visibility',
    icon: 'M',
    questions: [
      { id: 'mn1', text: 'Do you monitor at the service level (not just individual hosts or components)?', article: 'FCA SS1/21 §7.2', weight: 10, recommendation: 'Implement service-level health indicators that reflect the customer experience. Aggregate component metrics into service-level dashboards that show whether the service is meeting its tolerances.' },
      { id: 'mn2', text: 'Are alerts tied directly to impact tolerance thresholds?', article: 'FCA SS1/21 §7.3 / DORA Art. 10', weight: 10, recommendation: 'Configure alerting thresholds based on impact tolerances, not arbitrary values. Alert when a service is approaching its tolerance, not just when components fail.' },
      { id: 'mn3', text: 'Can you see real-time tolerance status for each important business service?', article: 'FCA SS1/21 §7.4', weight: 9, recommendation: 'Build dashboards showing current disruption duration and degradation against tolerance thresholds for each service. Make these accessible to incident commanders and senior management.' },
      { id: 'mn4', text: 'Is third-party health monitored against contracted SLAs?', article: 'DORA Art. 28(5) / FCA SS1/21 §7.5', weight: 9, recommendation: 'Track third-party provider performance against their SLA commitments. Include uptime, latency, and error rates. Alert when providers approach or breach their SLAs.' },
      { id: 'mn5', text: 'Is there anomaly detection for identifying emerging disruptions before they breach tolerances?', article: 'DORA Art. 10(1)', weight: 8, recommendation: 'Implement anomaly detection that identifies unusual patterns in service metrics. Early warning enables proactive response before customers are affected.' },
      { id: 'mn6', text: 'Are monitoring systems themselves resilient (not a single point of failure)?', article: 'DORA Art. 10(2)', weight: 8, recommendation: 'Ensure monitoring infrastructure has its own redundancy. If your monitoring goes down during an incident, you lose visibility exactly when you need it most.' },
      { id: 'mn7', text: 'Are tolerance breaches reported to the board in a timely manner?', article: 'FCA SS1/21 §7.6 / DORA Art. 5(2)', weight: 9, recommendation: 'Establish board reporting triggers for tolerance breaches. Define what constitutes a reportable breach and the timeline for escalation to board level.' },
      { id: 'mn8', text: 'Do you monitor end-to-end transactions across the full service chain?', article: 'FCA SS1/21 §7.7', weight: 8, recommendation: 'Implement distributed tracing or synthetic transactions that traverse the entire service chain. This reveals bottlenecks and failures that component-level monitoring misses.' },
      { id: 'mn9', text: 'Are incident detection times measured and compared against expectations?', article: 'DORA Art. 17(1)', weight: 7, recommendation: 'Track mean time to detect (MTTD) for each service. Set detection time targets and monitor trends. Slow detection extends disruption beyond tolerance.' },
    ],
  },
  {
    id: 'proof',
    title: 'Proof',
    description: 'Evidence through scenario testing, postmortems, and resilience dossiers',
    icon: 'P',
    questions: [
      { id: 'pr1', text: 'Do you conduct regular scenario testing for each important business service?', article: 'FCA SS1/21 §8.2 / DORA Art. 24', weight: 10, recommendation: 'Design and execute scenario tests that simulate disruptions to each important business service. Test at least annually, with critical services tested more frequently.' },
      { id: 'pr2', text: 'Does testing cover end-to-end service chains (not just individual components)?', article: 'FCA SS1/21 §8.3 / DORA Art. 24(2)', weight: 10, recommendation: 'Ensure tests exercise the full service chain including third-party dependencies. Component tests alone miss integration failures and cascading effects.' },
      { id: 'pr3', text: 'Are test results documented with identified gaps and remediation plans?', article: 'FCA SS1/21 §8.5 / DORA Art. 24(5)', weight: 9, recommendation: 'Maintain formal test reports with findings, remediation actions, owners, and timelines. Track remediation to completion.', toolRef: '/tools/incident-postmortem' },
      { id: 'pr4', text: 'Do you maintain a service resilience dossier for each important business service?', article: 'FCA SS1/21 §9', weight: 9, recommendation: 'Compile a per-service evidence pack including: service definition, dependency maps, impact tolerances, test results, incident history, remediation status, and board sign-off.' },
      { id: 'pr5', text: 'Are postmortems conducted after tolerance breaches or near-misses?', article: 'FCA SS1/21 §8.7 / DORA Art. 17(3)', weight: 9, recommendation: 'Run blameless postmortems after any tolerance breach or near-miss. Focus on systemic improvements, not individual blame.', toolRef: '/tools/incident-postmortem' },
      { id: 'pr6', text: 'Is remediation tracked with clear ownership and timelines?', article: 'FCA SS1/21 §8.6 / DORA Art. 24(5)', weight: 9, recommendation: 'Use a tracking system for resilience remediation items. Each item needs an owner, deadline, and regular status updates. Report overdue items to senior management.' },
      { id: 'pr7', text: 'Do you conduct threat-led penetration testing (TLPT) where required?', article: 'DORA Art. 26', weight: 8, recommendation: 'For in-scope entities, commission TLPT from qualified external testers using real-world threat intelligence. DORA requires this at least every 3 years for critical functions.' },
      { id: 'pr8', text: 'Can you demonstrate continuous improvement through trend data across test cycles?', article: 'FCA SS1/21 §8.8 / DORA Art. 24(1)', weight: 8, recommendation: 'Track resilience metrics over time: test pass rates, recovery times, gap closure rates. Show a positive trend to regulators as evidence of a maturing programme.' },
      { id: 'pr9', text: 'Is testing independently reviewed or audited?', article: 'DORA Art. 24(6)', weight: 7, recommendation: 'Have internal audit or external assessors review your testing programme for adequacy. Ensure testers are independent from the teams who built the systems.' },
      { id: 'pr10', text: 'Is there a formal process for reporting to regulators (incident and resilience reporting)?', article: 'FCA PS21/3 §5 / DORA Art. 19', weight: 8, recommendation: 'Document the process for regulatory reporting including triggers, templates, timelines, and responsible individuals. Test the reporting process itself periodically.' },
    ],
  },
];

const relatedTools = [
  { href: '/tools/dora-compliance', title: 'DORA Compliance Assessment' },
  { href: '/tools/vendor-risk-assessment', title: 'Vendor Risk Assessment' },
  { href: '/tools/incident-response-planner', title: 'Incident Response Planner' },
  { href: '/tools/incident-postmortem', title: 'Incident Postmortem' },
  { href: '/tools/dependency-mapper', title: 'Dependency Mapper' },
  { href: '/tools/business-continuity-planner', title: 'Business Continuity Planner' },
  { href: '/tools/sla-generator', title: 'SLA Generator' },
  { href: '/tools/archimate', title: 'ArchiMate Modeler' },
];

export default function STAMPFramework() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [expandedGaps, setExpandedGaps] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'stamp-framework',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const toggleGap = (id: string) => {
    setExpandedGaps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { id: string; article: string; question: string; status: string; weight: number; recommendation: string; toolRef?: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ id: q.id, article: q.article, question: q.text, status: 'Partial', weight: q.weight, recommendation: q.recommendation, toolRef: q.toolRef });
        } else if (answer === 'no') {
          gaps.push({ id: q.id, article: q.article, question: q.text, status: 'Not Implemented', weight: q.weight, recommendation: q.recommendation, toolRef: q.toolRef });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => b.weight - a.weight),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getReadinessLevel = (score: number) => {
    if (score >= 90) return { level: 'Resilient', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 75) return { level: 'Substantially Resilient', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    if (score >= 60) return { level: 'Developing', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 40) return { level: 'Foundational', color: 'text-accent', bg: 'bg-accent/20' };
    return { level: 'Unprepared', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const getGapPriority = (weight: number) => {
    if (weight >= 10) return { label: 'Critical', color: 'text-red-400' };
    if (weight >= 8) return { label: 'High', color: 'text-accent' };
    return { label: 'Medium', color: 'text-yellow-400' };
  };

  const handleSaveToCommandCenter = async () => {
    const { score, gaps } = calculateScore();
    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const result = await saveResult({
      name: 'STAMP Operational Resilience Assessment',
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gaps.map((g) => ({
        id: g.id,
        title: g.question,
        severity: g.weight >= 10 ? 'critical' : g.weight >= 8 ? 'high' : 'medium',
        description: `${g.article} — ${g.recommendation}`,
      })),
      recommendations: score < 90 ? [
        { id: 'rec-1', title: 'Complete important business service identification and mapping', priority: 'high' },
        { id: 'rec-2', title: 'Set and calibrate impact tolerances for all services', priority: 'high' },
        { id: 'rec-3', title: 'Identify and mitigate single points of failure', priority: 'high' },
        { id: 'rec-4', title: 'Implement service-level monitoring tied to tolerances', priority: 'medium' },
        { id: 'rec-5', title: 'Build resilience dossiers with testing evidence', priority: 'medium' },
      ] : [],
      metadata: {},
    });

    if (result) setSaved(true);
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const readiness = getReadinessLevel(score);

    let md = `# STAMP Operational Resilience Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n`;
    md += `**Framework:** STAMP (Services, Tolerances, Architecture, Monitoring, Proof)\n`;
    md += `**Regulatory Alignment:** FCA PS21/3, FCA SS1/21, EU DORA\n\n`;

    md += `## Overall Readiness Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Status:** ${readiness.level}\n\n`;

    md += `## Pillar Scores\n\n`;
    md += `| STAMP Pillar | Score |\n`;
    md += `|---|---|\n`;
    sections.forEach((section) => {
      md += `| **${section.icon}** — ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Gaps & Recommendations\n\n`;
      gaps.forEach((gap) => {
        const priority = getGapPriority(gap.weight);
        md += `### [${priority.label}] ${gap.question}\n\n`;
        md += `- **Regulation:** ${gap.article}\n`;
        md += `- **Status:** ${gap.status}\n`;
        md += `- **Recommendation:** ${gap.recommendation}\n`;
        if (gap.toolRef) {
          md += `- **Related Tool:** https://theartofcto.com${gap.toolRef}\n`;
        }
        md += `\n`;
      });
    }

    md += `## Recommended Actions\n\n`;
    if (score < 90) {
      md += `1. Complete important business service identification and dependency mapping\n`;
      md += `2. Set impact tolerances calibrated to severe-but-plausible scenarios\n`;
      md += `3. Conduct SPOF analysis and implement failure isolation\n`;
      md += `4. Build service-level monitoring dashboards tied to tolerance thresholds\n`;
      md += `5. Establish scenario testing programme and compile resilience dossiers\n`;
      md += `6. Ensure board approval and regular review cycles\n`;
    } else {
      md += `1. Maintain current resilience controls and documentation\n`;
      md += `2. Continue regular scenario testing and tolerance recalibration\n`;
      md += `3. Monitor regulatory guidance updates from FCA and ESAs\n`;
      md += `4. Track continuous improvement metrics across test cycles\n`;
    }

    md += `\n---\n\n`;
    md += `## Service Resilience Dossier Template\n\n`;
    md += `Use this template for each important business service to compile evidence for regulators.\n\n`;
    md += `### 1. Service Definition\n\n`;
    md += `- **Service Name:** _________________________\n`;
    md += `- **Service Owner:** _________________________\n`;
    md += `- **Customer Groups Served:** _________________________\n`;
    md += `- **Board Approval Date:** _________________________\n\n`;
    md += `### 2. Dependency Map\n\n`;
    md += `- **People:** _________________________\n`;
    md += `- **Processes:** _________________________\n`;
    md += `- **Technology:** _________________________\n`;
    md += `- **Facilities:** _________________________\n`;
    md += `- **Data:** _________________________\n`;
    md += `- **Third-Party Providers:** _________________________\n\n`;
    md += `### 3. Impact Tolerances\n\n`;
    md += `| Metric | Tolerance | Rationale |\n`;
    md += `|---|---|---|\n`;
    md += `| Maximum Outage Duration | _________ | _________ |\n`;
    md += `| Maximum Degraded-Mode Duration | _________ | _________ |\n`;
    md += `| Maximum Data Loss (RPO) | _________ | _________ |\n`;
    md += `| Minimum Throughput in Degraded Mode | _________ | _________ |\n\n`;
    md += `### 4. Single Points of Failure\n\n`;
    md += `| SPOF | Impact | Mitigation | Status |\n`;
    md += `|---|---|---|---|\n`;
    md += `| _________ | _________ | _________ | _________ |\n\n`;
    md += `### 5. Test Results\n\n`;
    md += `| Date | Scenario | Outcome | Remediation |\n`;
    md += `|---|---|---|---|\n`;
    md += `| _________ | _________ | _________ | _________ |\n\n`;
    md += `### 6. Incident History\n\n`;
    md += `| Date | Incident | Tolerance Breached? | Postmortem Link |\n`;
    md += `|---|---|---|---|\n`;
    md += `| _________ | _________ | _________ | _________ |\n\n`;
    md += `### 7. Remediation Tracker\n\n`;
    md += `| Item | Owner | Deadline | Status |\n`;
    md += `|---|---|---|---|\n`;
    md += `| _________ | _________ | _________ | _________ |\n\n`;

    md += `---\n*Assessment conducted using The Art of CTO STAMP Operational Resilience Assessment*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stamp-resilience-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              FCA &amp; DORA Operational Resilience
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              STAMP Operational Resilience Assessment
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your operational resilience maturity across Services, Tolerances, Architecture, Monitoring,
              and Proof — aligned to FCA PS21/3, FCA SS1/21, and EU DORA requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">What is STAMP?</h3>
              <p className="text-foreground-secondary text-sm">
                STAMP is a CTO-oriented framework for operational resilience that organises
                the requirements of FCA (UK) and DORA (EU) into five actionable pillars:
                Services, Tolerances, Architecture, Monitoring, and Proof.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Who Should Use This?</h3>
              <p className="text-foreground-secondary text-sm">
                CTOs, CIOs, and technology leaders in financial services firms subject to
                FCA operational resilience rules (UK) or the Digital Operational Resilience
                Act (EU). Also useful for their critical ICT third-party providers.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">The Five STAMP Pillars</h3>
            <div className="space-y-3 text-sm text-foreground-secondary">
              {sections.map((section) => (
                <div key={section.id} className="flex gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded bg-accent/10 text-accent font-bold flex items-center justify-center text-lg">
                    {section.icon}
                  </span>
                  <div>
                    <span className="text-foreground font-medium">{section.title}</span>
                    <p>{section.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Regulatory Context</h3>
            <div className="text-foreground-secondary text-sm space-y-2">
              <p>
                <span className="text-foreground font-medium">FCA (UK):</span>{' '}
                Operational resilience requirements under PS21/3 and SS1/21 are fully in force.
                Firms must remain within impact tolerances by 31 March 2025.
              </p>
              <p>
                <span className="text-foreground font-medium">DORA (EU):</span>{' '}
                The Digital Operational Resilience Act applies from 17 January 2025 to all
                EU financial entities and their critical ICT providers.
              </p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start STAMP Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              47 questions across 5 pillars — takes approximately 20-30 minutes
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => currentSection === 0 ? setView('landing') : setCurrentSection(currentSection - 1)}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Pillar {currentSection + 1} of {sections.length}
            </span>
          </div>

          {/* STAMP Letter Progress */}
          <div className="flex justify-center gap-2 mb-8">
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setCurrentSection(i)}
                className={`w-12 h-12 rounded-lg font-bold text-xl transition-colors ${
                  i === currentSection
                    ? 'bg-accent text-accent-foreground'
                    : i < currentSection
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'bg-surface border border-border-muted text-foreground-tertiary'
                }`}
              >
                {s.icon}
              </button>
            ))}
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-foreground/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-10 rounded bg-accent/10 text-accent font-bold flex items-center justify-center text-2xl">
                {section.icon}
              </span>
              <h2 className="text-xl font-semibold text-foreground">{section.title}</h2>
            </div>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.article}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-foreground/5 text-foreground-tertiary border border-border-muted hover:border-border'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-accent transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Pillar
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results view
  const { score, gaps } = calculateScore();
  const readiness = getReadinessLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-foreground/10 hover:bg-foreground/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            STAMP Resilience Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${readiness.bg}`}>
            <span className={`text-4xl font-bold ${readiness.color}`}>{score}%</span>
            <p className={`${readiness.color} font-medium mt-1`}>{readiness.level}</p>
          </div>
        </div>

        {/* STAMP Pillar Scores */}
        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Pillar Scores</h2>

          {/* STAMP letter summary */}
          <div className="flex justify-center gap-3 mb-6">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionReadiness = getReadinessLevel(sectionScore);
              return (
                <div key={section.id} className="text-center">
                  <div className={`w-14 h-14 rounded-lg font-bold text-2xl flex items-center justify-center ${sectionReadiness.bg} ${sectionReadiness.color}`}>
                    {section.icon}
                  </div>
                  <span className={`text-sm font-medium ${sectionReadiness.color}`}>{sectionScore}%</span>
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionReadiness = getReadinessLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">
                      <span className="text-accent font-bold mr-2">{section.icon}</span>
                      {section.title}
                    </span>
                    <span className={sectionReadiness.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-foreground/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 75
                          ? 'bg-blue-500'
                          : sectionScore >= 60
                          ? 'bg-yellow-500'
                          : sectionScore >= 40
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Gaps with Expandable Recommendations */}
        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Gaps &amp; Recommendations ({gaps.length})
            </h2>
            <div className="space-y-3">
              {gaps.map((gap) => {
                const priority = getGapPriority(gap.weight);
                const isExpanded = expandedGaps[gap.id] ?? false;
                return (
                  <div
                    key={gap.id}
                    className="border border-border-muted rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleGap(gap.id)}
                      className="w-full p-3 text-left flex items-start justify-between gap-4 hover:bg-foreground/5 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-accent text-xs font-medium">
                            {gap.article}
                          </span>
                          <span className={`text-xs font-medium ${priority.color}`}>
                            {priority.label}
                          </span>
                        </div>
                        <p className="text-foreground text-sm">{gap.question}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded ${
                            gap.status === 'Partial'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {gap.status}
                        </span>
                        <span className="text-foreground-tertiary text-xs">
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-border-muted pt-3">
                        <p className="text-foreground-secondary text-sm mb-2">
                          {gap.recommendation}
                        </p>
                        {gap.toolRef && (
                          <Link
                            href={gap.toolRef}
                            className="text-accent hover:underline text-sm inline-flex items-center gap-1"
                          >
                            Open related tool →
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Readiness Levels Reference */}
        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Readiness Levels</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            {[
              { label: 'Resilient', range: '90%+', color: 'text-green-400', bg: 'bg-green-500/10' },
              { label: 'Substantially Resilient', range: '75-89%', color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { label: 'Developing', range: '60-74%', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
              { label: 'Foundational', range: '40-59%', color: 'text-accent', bg: 'bg-accent/10' },
              { label: 'Unprepared', range: '<40%', color: 'text-red-400', bg: 'bg-red-500/10' },
            ].map((level) => (
              <div key={level.label} className={`${level.bg} rounded-lg p-3 text-center`}>
                <span className={`font-medium ${level.color}`}>{level.label}</span>
                <p className="text-foreground-tertiary text-xs mt-1">{level.range}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related Tools */}
        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Related Tools</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {relatedTools.map((tool) => (
              <Link
                key={tool.href}
                href={tool.href}
                className="border border-border-muted rounded-lg p-3 text-sm text-foreground-secondary hover:text-accent hover:border-accent/30 transition-colors"
              >
                {tool.title}
              </Link>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <button
            onClick={exportToMarkdown}
            className="px-6 py-3 bg-foreground/10 hover:bg-foreground/20 text-foreground rounded-lg transition-colors"
          >
            Export Report
          </button>
          {commandCenterEnabled && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving || saved}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
            </button>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setExpandedGaps({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
